import { Fragment, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Pencil, RotateCcw } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent } from "@v2/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { supabaseV2 } from "../../lib/supabase";
import { usePermissions } from "../../lib/permissions";
import { seasonErrorMessage, seasonPeriodLabel, useSeason, type Season } from "../../lib/seasons";
import { EmptyState, SectionLoading } from "../../components/state";
import { SeasonReport } from "./SeasonReport";
import { CloseSeasonWizard } from "./CloseSeasonWizard";

export function SeasonsView({ teamId, onOpenPlayer }: { teamId: string; onOpenPlayer: (playerId: string) => void }) {
  const { t } = useTranslation("seasons");
  const queryClient = useQueryClient();
  // canManageMembers = founder della squadra o app admin: stesso perimetro di is_team_founder lato DB.
  const { canManageMembers: isFounder } = usePermissions();
  const { seasons, activeSeason, selectedSeason, selectSeason, ready } = useSeason();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<Season | null>(null);

  const latestClosed = seasons.find((s) => s.status === "closed") || null;
  const ordered = [...seasons].sort((a, b) => b.starts_on.localeCompare(a.starts_on));
  const previousOf = (season: Season) => ordered.find((s) => s.starts_on < season.starts_on) || null;

  const reopen = useMutation({
    mutationFn: async (season: Season) => {
      const { error } = await supabaseV2.rpc("reopen_season", { _season_id: season.id });
      if (error) throw error;
    },
    onSuccess: (_data, season) => {
      invalidateTeamData(queryClient, teamId);
      selectSeason(season.id);
    },
    onError: (e: any) => window.alert(seasonErrorMessage(e) || e?.message || String(e)),
  });

  const onReopen = (season: Season) => {
    const message = activeSeason
      ? t("reopen.confirmMerge", { closed: season.name, active: activeSeason.name })
      : t("reopen.confirm", { closed: season.name });
    if (window.confirm(message)) reopen.mutate(season);
  };

  if (!ready) return <SectionLoading />;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-sm text-muted-foreground">{t("view.subtitle")}</p>
      </div>

      {seasons.length === 0 ? (
        <EmptyState text={t("view.empty")} />
      ) : (
        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {ordered.map((season) => {
            const isSelected = season.id === selectedSeason?.id;
            const s = season.stats;
            return (
              <Fragment key={season.id}>
                <Card className={cn(isSelected && "ring-1 ring-[hsl(var(--input))]")}>
                  <CardContent className="p-5 space-y-4">
                    <button type="button" className="w-full text-left" onClick={() => selectSeason(season.id)} aria-pressed={isSelected}>
                      <span className={cn("inline-flex items-center gap-2 text-sm", season.status === "active" ? "text-[hsl(var(--ok))]" : "text-muted-foreground")}>
                        <span className={cn("eb-dot", season.status === "active" ? "dot-ok" : "dot-muted")} />
                        {season.status === "active" ? t("status.active") : t("status.closed")}
                      </span>
                      <span className="eb-num block text-[44px] pr-1 mt-1.5 break-words">{season.name}</span>
                      <span className="block mt-1 text-[13px] text-muted-foreground">{seasonPeriodLabel(season)}</span>
                    </button>
                    <dl className="grid grid-cols-3 gap-3 pt-1">
                      <MiniStat label={t("card.trainings")} value={s?.trainings ?? 0} />
                      <MiniStat label={t("card.matches")} value={s?.matches ?? 0} />
                      <MiniStat label={t("card.record")} value={`${s?.wins ?? 0}·${s?.draws ?? 0}·${s?.losses ?? 0}`} />
                    </dl>
                    {isFounder && (
                      <div className="flex flex-wrap gap-1 pt-3 border-t -mx-1">
                        <Button size="sm" variant="ghost" className="text-foreground px-3" onClick={() => setRenameTarget(season)}>
                          <Pencil className="h-4 w-4" />
                          {t("card.rename")}
                        </Button>
                        {latestClosed?.id === season.id && (
                          <Button size="sm" variant="ghost" className="text-foreground px-3" disabled={reopen.isPending} onClick={() => onReopen(season)}>
                            <RotateCcw className="h-4 w-4" />
                            {t("card.reopen")}
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                {isFounder && season.id === activeSeason?.id && (
                  <section className="rounded-[20px] bg-raised p-5 flex flex-col">
                    <h2 className="font-semibold">{t("view.closeTitle")}</h2>
                    <p className="mt-1 mb-4 text-[13px] text-muted-foreground">{t("view.closeDesc")}</p>
                    <Button variant="secondary" onClick={() => setWizardOpen(true)} className="mt-auto w-full bg-background hover:bg-background/70">
                      {t("view.closeCta")}
                    </Button>
                  </section>
                )}
              </Fragment>
            );
          })}
        </section>
      )}

      {selectedSeason && (
        <SeasonReport
          teamId={teamId}
          season={selectedSeason}
          previous={previousOf(selectedSeason)}
          onOpenPlayer={onOpenPlayer}
        />
      )}

      {isFounder && activeSeason && (
        <CloseSeasonWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          teamId={teamId}
          season={activeSeason}
          existingNames={seasons.filter((s) => s.id !== activeSeason.id).map((s) => s.name)}
          onClosed={(newSeasonId) => {
            invalidateTeamData(queryClient, teamId);
            selectSeason(newSeasonId);
          }}
        />
      )}

      <RenameSeasonDialog
        season={renameTarget}
        onOpenChange={(open) => !open && setRenameTarget(null)}
        onRenamed={() => queryClient.invalidateQueries({ queryKey: ["seasons", teamId] })}
      />
    </div>
  );
}

/** Dopo chiusura/riapertura cambiano stagioni e assegnazioni: si ricarica tutto il perimetro squadra. */
function invalidateTeamData(queryClient: ReturnType<typeof useQueryClient>, teamId: string) {
  for (const key of ["seasons", "events", "responses", "matches", "match-player-stats", "event-callups", "player-events"]) {
    queryClient.invalidateQueries({ queryKey: [key, teamId] });
  }
  queryClient.invalidateQueries({ queryKey: ["player-match-stats"] });
  queryClient.invalidateQueries({ queryKey: ["player-responses"] });
}

function MiniStat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex flex-col-reverse justify-end min-w-0">
      <dt className="text-[13px] leading-tight text-[hsl(var(--faint))] truncate">{label}</dt>
      <dd className="eb-num text-[28px] pr-1 truncate">{value}</dd>
    </div>
  );
}

function RenameSeasonDialog({
  season,
  onOpenChange,
  onRenamed,
}: {
  season: Season | null;
  onOpenChange: (open: boolean) => void;
  onRenamed: () => void;
}) {
  const { t } = useTranslation("seasons");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!season) return;
    setName(season.name);
    setError(null);
  }, [season]);

  const rename = useMutation({
    mutationFn: async () => {
      if (!season) return;
      const { error: rpcError } = await supabaseV2.rpc("rename_season", { _season_id: season.id, _name: name });
      if (rpcError) throw rpcError;
    },
    onSuccess: () => {
      onRenamed();
      onOpenChange(false);
    },
    onError: (e: any) => setError(seasonErrorMessage(e) || e?.message || String(e)),
  });

  return (
    <Dialog open={!!season} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("rename.title")}</DialogTitle>
          <DialogDescription>{t("rename.description")}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            rename.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="season-rename">{t("rename.label")}</Label>
            <Input id="season-rename" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={rename.isPending || !name.trim()}>
              {t("rename.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
